"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Card,
  ColorSwatch,
  Group,
  Button,
  Anchor,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

export default function BestColorsForSkinToneContent() {
  const t = useTranslations("bestColorsForSkinTone");

  return (
    <Container size="md" py="xl">
      {/* Hero */}
      <Stack gap="md" ta="center" maw={600} mx="auto" mb={60}>
        <Title order={1}>{t("hero.title")}</Title>
        <Text c="dimmed" size="lg">
          {t("hero.subtitle")}
        </Text>
        <Button
          component={Link}
          href="/color-analysis-quiz"
          size="lg"
          color="teal"
          rightSection={<IconArrowRight size="1rem" />}
        >
          {t("hero.ctaButton")}
        </Button>
      </Stack>

      {/* Intro */}
      <Stack gap="lg" mb={60}>
        <Text>{t("intro.paragraph1")}</Text>
        <Text>{t("intro.paragraph2")}</Text>
      </Stack>

      {/* How to Know What Colors Look Good on You */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("howToKnow.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("howToKnow.subtitle")}
        </Text>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg" mb="lg">
          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t("howToKnow.step1Title")}
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              {t("howToKnow.step1Intro")}
            </Text>
            <Stack gap="xs">
              <Text size="sm">{t("howToKnow.step1Warm")}</Text>
              <Text size="sm">{t("howToKnow.step1Cool")}</Text>
              <Text size="sm">{t("howToKnow.step1Neutral")}</Text>
            </Stack>
            <Anchor component={Link} href="/undertone-quiz" c="teal" size="sm" mt="md">
              {t("howToKnow.step1Link")}
            </Anchor>
          </Card>

          <Card withBorder padding="lg" radius="md">
            <Text fw={600} mb="sm">
              {t("howToKnow.step2Title")}
            </Text>
            <Text size="sm" c="dimmed" mb="sm">
              {t("howToKnow.step2Intro")}
            </Text>
            <Stack gap="xs">
              <Text size="sm">{t("howToKnow.step2High")}</Text>
              <Text size="sm">{t("howToKnow.step2Low")}</Text>
              <Text size="sm">{t("howToKnow.step2Medium")}</Text>
            </Stack>
          </Card>
        </SimpleGrid>

        <Text c="dimmed">{t("howToKnow.conclusion")}</Text>
      </Box>

      {/* Best Colors for Warm Skin Tones */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("warmTones.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("warmTones.intro")}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb="lg">
          <Card withBorder padding="lg" radius="md">
            <Group gap="xs" mb="sm">
              {["#FF7F50", "#FFD700", "#32CD32", "#FF6347", "#D2691E", "#F4A460"].map((c) => (
                <ColorSwatch key={c} color={c} size={24} />
              ))}
            </Group>
            <Text fw={600} mb="xs">
              {t("warmTones.springTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("warmTones.springColors")}
            </Text>
            <Anchor component={Link} href="/color-palette/spring/true" c="teal" size="sm" mt="sm">
              {t("warmTones.springLink")}
            </Anchor>
          </Card>

          <Card withBorder padding="lg" radius="md">
            <Group gap="xs" mb="sm">
              {["#8B4513", "#CD853F", "#556B2F", "#B8860B", "#A0522D", "#6B8E23"].map((c) => (
                <ColorSwatch key={c} color={c} size={24} />
              ))}
            </Group>
            <Text fw={600} mb="xs">
              {t("warmTones.autumnTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("warmTones.autumnColors")}
            </Text>
            <Anchor component={Link} href="/color-palette/autumn/true" c="teal" size="sm" mt="sm">
              {t("warmTones.autumnLink")}
            </Anchor>
          </Card>
        </SimpleGrid>

        <Card withBorder padding="md" radius="md" bg="red.0">
          <Text fw={600} mb="xs" c="red.7">
            {t("warmTones.avoidTitle")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("warmTones.avoidText")}
          </Text>
        </Card>
      </Box>

      {/* Best Colors for Cool Skin Tones */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("coolTones.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("coolTones.intro")}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mb="lg">
          <Card withBorder padding="lg" radius="md">
            <Group gap="xs" mb="sm">
              {["#E6E6FA", "#B0C4DE", "#DDA0DD", "#778899", "#BC8F8F", "#9370DB"].map((c) => (
                <ColorSwatch key={c} color={c} size={24} />
              ))}
            </Group>
            <Text fw={600} mb="xs">
              {t("coolTones.summerTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("coolTones.summerColors")}
            </Text>
            <Anchor component={Link} href="/color-palette/summer/true" c="teal" size="sm" mt="sm">
              {t("coolTones.summerLink")}
            </Anchor>
          </Card>

          <Card withBorder padding="lg" radius="md">
            <Group gap="xs" mb="sm">
              {["#FF0000", "#0000CD", "#4169E1", "#C71585", "#008B8B", "#191970"].map((c) => (
                <ColorSwatch key={c} color={c} size={24} />
              ))}
            </Group>
            <Text fw={600} mb="xs">
              {t("coolTones.winterTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("coolTones.winterColors")}
            </Text>
            <Anchor component={Link} href="/color-palette/winter/true" c="teal" size="sm" mt="sm">
              {t("coolTones.winterLink")}
            </Anchor>
          </Card>
        </SimpleGrid>

        <Card withBorder padding="md" radius="md" bg="red.0">
          <Text fw={600} mb="xs" c="red.7">
            {t("coolTones.avoidTitle")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("coolTones.avoidText")}
          </Text>
        </Card>
      </Box>

      {/* Best Colors for Neutral Skin Tones */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("neutralTones.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("neutralTones.intro")}
        </Text>

        <Card withBorder padding="lg" radius="md" mb="lg">
          <Group gap="xs" mb="sm">
            {["#D2B48C", "#8FBC8F", "#BC8F8F", "#708090", "#DEB887", "#5F9EA0"].map((c) => (
              <ColorSwatch key={c} color={c} size={24} />
            ))}
          </Group>
          <Text fw={600} mb="xs">
            {t("neutralTones.bestTitle")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("neutralTones.bestColors")}
          </Text>
        </Card>

        <Text c="dimmed">
          {t("neutralTones.conclusion").split("Soft Autumn")[0]}
          <Anchor component={Link} href="/color-palette/autumn/soft" c="teal">
            {t("neutralTones.softAutumnLink")}
          </Anchor>
          {" "}or{" "}
          <Anchor component={Link} href="/color-palette/summer/cool" c="teal">
            {t("neutralTones.softSummerLink")}
          </Anchor>
          . {t("neutralTones.conclusion").split("Soft Summer. ")[1]}
        </Text>
      </Box>

      {/* Color Theory for Skin Tones */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("colorTheory.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("colorTheory.intro")}
        </Text>

        <Stack gap="md">
          <Box>
            <Text fw={600} mb="xs">
              {t("colorTheory.reflectionTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("colorTheory.reflectionText")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb="xs">
              {t("colorTheory.contrastTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("colorTheory.contrastText")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb="xs">
              {t("colorTheory.seasonTitle")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("colorTheory.seasonText").split("Take our quiz")[0]}
              <Anchor component={Link} href="/color-analysis-quiz" c="teal">
                {t("colorTheory.quizLink")}
              </Anchor>
              {" "}{t("colorTheory.seasonText").split("Take our quiz ")[1]}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* How to Test Colors at Home */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg">
          {t("testing.title")}
        </Title>
        <Text c="dimmed" mb="lg">
          {t("testing.intro")}
        </Text>

        <Stack gap="md">
          <Card withBorder padding="md" radius="md">
            <Text fw={600} mb="xs">
              {t("testing.test1Title")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("testing.test1Text")}
            </Text>
          </Card>

          <Card withBorder padding="md" radius="md">
            <Text fw={600} mb="xs">
              {t("testing.test2Title")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("testing.test2Text")}
            </Text>
          </Card>

          <Card withBorder padding="md" radius="md">
            <Text fw={600} mb="xs">
              {t("testing.test3Title")}
            </Text>
            <Text size="sm" c="dimmed">
              {t("testing.test3Text")}
            </Text>
          </Card>
        </Stack>
      </Box>

      {/* Find Your Exact Colors */}
      <Box ta="center" py={40} mb={40}>
        <Title order={2} size="h3" mb="md">
          {t("cta.title")}
        </Title>
        <Text c="dimmed" mb="lg" maw={500} mx="auto">
          {t("cta.description")}
        </Text>
        <Group justify="center" gap="md">
          <Button
            component={Link}
            href="/color-analysis-quiz"
            size="lg"
            color="teal"
            rightSection={<IconArrowRight size="1rem" />}
          >
            {t("cta.quizButton")}
          </Button>
          <Button
            component={Link}
            href="/ai-color-analysis"
            size="lg"
            variant="light"
            color="teal"
          >
            {t("cta.aiButton")}
          </Button>
        </Group>
      </Box>

      {/* Related Tools */}
      <Box mb={40}>
        <Title order={2} size="h3" mb="lg" ta="center">
          More Ways to Find Your Best Colors
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Anchor component={Link} href="/skin-tone-test" underline="never">
            <Card withBorder padding="md" radius="md" className="hover-lift">
              <Text fw={600} size="sm">Skin Tone Test</Text>
              <Text size="xs" c="dimmed">Learn DIY methods to determine your undertone at home</Text>
            </Card>
          </Anchor>
          <Anchor component={Link} href="/gold-or-silver-jewelry-test" underline="never">
            <Card withBorder padding="md" radius="md" className="hover-lift">
              <Text fw={600} size="sm">Gold or Silver Jewelry Test</Text>
              <Text size="xs" c="dimmed">Find out which metal complements your undertone</Text>
            </Card>
          </Anchor>
          <Anchor component={Link} href="/undertone-quiz" underline="never">
            <Card withBorder padding="md" radius="md" className="hover-lift">
              <Text fw={600} size="sm">Undertone Quiz</Text>
              <Text size="xs" c="dimmed">Quick quiz to determine warm, cool, or neutral</Text>
            </Card>
          </Anchor>
          <Anchor component={Link} href="/style-personality-quiz" underline="never">
            <Card withBorder padding="md" radius="md" className="hover-lift">
              <Text fw={600} size="sm">Style Personality Quiz</Text>
              <Text size="xs" c="dimmed">Discover your fashion archetype for wardrobe guidance</Text>
            </Card>
          </Anchor>
        </SimpleGrid>
      </Box>

      {/* Browse by Season */}
      <Box>
        <Title order={2} size="h3" mb="lg" ta="center">
          {t("browseBySeason.title")}
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Anchor component={Link} href="/color-palette/spring/true" underline="never">
            <Card withBorder padding="md" radius="md" ta="center" className="hover-lift">
              <Group gap={4} justify="center" mb="xs">
                {["#FF7F50", "#FFD700", "#32CD32"].map((c) => (
                  <ColorSwatch key={c} color={c} size={16} />
                ))}
              </Group>
              <Text fw={600} size="sm">
                {t("browseBySeason.spring")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("browseBySeason.springSubtitle")}
              </Text>
            </Card>
          </Anchor>

          <Anchor component={Link} href="/color-palette/summer/true" underline="never">
            <Card withBorder padding="md" radius="md" ta="center" className="hover-lift">
              <Group gap={4} justify="center" mb="xs">
                {["#B0C4DE", "#DDA0DD", "#778899"].map((c) => (
                  <ColorSwatch key={c} color={c} size={16} />
                ))}
              </Group>
              <Text fw={600} size="sm">
                {t("browseBySeason.summer")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("browseBySeason.summerSubtitle")}
              </Text>
            </Card>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/true" underline="never">
            <Card withBorder padding="md" radius="md" ta="center" className="hover-lift">
              <Group gap={4} justify="center" mb="xs">
                {["#8B4513", "#CD853F", "#556B2F"].map((c) => (
                  <ColorSwatch key={c} color={c} size={16} />
                ))}
              </Group>
              <Text fw={600} size="sm">
                {t("browseBySeason.autumn")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("browseBySeason.autumnSubtitle")}
              </Text>
            </Card>
          </Anchor>

          <Anchor component={Link} href="/color-palette/winter/true" underline="never">
            <Card withBorder padding="md" radius="md" ta="center" className="hover-lift">
              <Group gap={4} justify="center" mb="xs">
                {["#FF0000", "#0000CD", "#FFFFFF"].map((c) => (
                  <ColorSwatch key={c} color={c} size={16} />
                ))}
              </Group>
              <Text fw={600} size="sm">
                {t("browseBySeason.winter")}
              </Text>
              <Text size="xs" c="dimmed">
                {t("browseBySeason.winterSubtitle")}
              </Text>
            </Card>
          </Anchor>
        </SimpleGrid>
      </Box>

      {/* Browse by Skin Tone */}
      <Box mb={60}>
        <Title order={2} size="h3" mb="lg" ta="center">
          Browse Color Guides by Skin Tone
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          {[
            { href: "/best-colors-for-pale-skin", label: "Pale Skin" },
            { href: "/best-colors-for-olive-skin", label: "Olive Skin" },
            { href: "/best-colors-for-tan-skin", label: "Tan Skin" },
            { href: "/best-colors-for-dark-skin", label: "Dark Skin" },
          ].map((item) => (
            <Anchor key={item.href} component={Link} href={item.href} underline="never">
              <Card withBorder p="md" radius="md" ta="center" className="hover-lift">
                <Text fw={600} size="sm">
                  {item.label}
                </Text>
                <Text size="xs" c="dimmed" mt={4}>
                  Best colors guide
                </Text>
              </Card>
            </Anchor>
          ))}
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="md">
          <Anchor component={Link} href="/best-hair-colors-for-skin-tone" underline="never">
            <Card withBorder p="md" radius="md" ta="center" className="hover-lift">
              <Text fw={600} size="sm">Best Hair Colors for Your Skin Tone</Text>
              <Text size="xs" c="dimmed" mt={4}>Find flattering hair color shades</Text>
            </Card>
          </Anchor>
          <Anchor component={Link} href="/best-makeup-colors-for" underline="never">
            <Card withBorder p="md" radius="md" ta="center" className="hover-lift">
              <Text fw={600} size="sm">Best Makeup Colors for Your Skin Tone</Text>
              <Text size="xs" c="dimmed" mt={4}>Foundation, lipstick, blush guide</Text>
            </Card>
          </Anchor>
        </SimpleGrid>
      </Box>

      {/* CSS for hover effects */}
      <style jsx global>{`
        .hover-lift:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .hover-lift {
          transition: transform 0.2s, box-shadow 0.2s;
        }
      `}</style>
    </Container>
  );
}
